import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode, Ref } from "react";
import { cx } from "../cx";

/** Tiers del sistema: sin variante = secundario (quieto por defecto en tablas: quiet). */
export type ButtonVariant = "primary" | "quiet" | "ghost" | "danger" | "link" | "go";
export interface ButtonStyle {
  variant?: ButtonVariant | undefined;
  size?: "s" | "l" | undefined;
  iconOnly?: boolean | undefined;
  loading?: boolean | undefined;
  block?: boolean | undefined;
  /** Dato/mando desactualizado: la clase apaga el ratón, y `aria-disabled` +
      `tabindex=-1` lo sacan del teclado — la clase sola no basta. */
  stale?: boolean | undefined;
  /** MATIZ de peligro sobre un enlace (`sk-danger`), no el tier `--danger`:
      el acto que retira o descarta dentro de una ficha o una fila. */
  danger?: boolean | undefined;
  /** Tono AL PASAR sobre un botón quieto (`sk-hov-danger` / `sk-hov-ok`): el
      acto es reversible y no merece el tier de peligro, pero avisa al
      acercarse («Desconectar» olvida el permiso y los resultados). */
  hov?: "danger" | "ok" | undefined;
  /** PLEGABLE (`sk-btn--fold`): por debajo de 560 px se queda en el icono;
      el rótulo va en `ButtonLabel` y sigue para el lector. */
  fold?: boolean | undefined;
}

/** La clase del botón — para componer sobre <Link> u otros elementos del router. */
export function buttonClass(s: ButtonStyle = {}, extra?: string): string {
  return cx("sk-btn", s.variant && `sk-btn--${s.variant}`, s.size && `sk-btn--${s.size}`, s.iconOnly && "sk-btn--icon", s.loading && "sk-loading", s.danger && "sk-danger", s.hov === "danger" && "sk-hov-danger", s.hov === "ok" && "sk-hov-ok", s.fold && "sk-btn--fold", extra);
}

/** `tip` (+ `tipPos`) pinta el tooltip del sistema (data-sk-tip) — motivo de un mando deshabilitado (visibilidad ≠ autorización). */
export interface ButtonTip { tip?: string | undefined; tipPos?: "bottom" | "right" | "left" | undefined }

export function Button({ variant, size, iconOnly, loading, block, stale, danger, hov, fold, className, type = "button", style, tip, tipPos, ...rest }: ButtonStyle & ButtonTip & { ref?: Ref<HTMLButtonElement> | undefined } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type={type}
      className={buttonClass({ variant, size, iconOnly, loading, danger, hov, fold }, cx(stale && "sk-stale", className))}
      aria-busy={loading || undefined}
      data-sk-tip={tip} data-sk-tip-pos={tip ? tipPos : undefined}
      style={block ? { width: "100%", ...style } : style}
      {...rest}
      {...(stale === true && { "aria-disabled": true, tabIndex: -1 })}
    />
  );
}

export function ButtonLink({ variant, size, iconOnly, loading, block, danger, fold, className, style, ...rest }: ButtonStyle & AnchorHTMLAttributes<HTMLAnchorElement>) {
  return <a className={buttonClass({ variant, size, iconOnly, loading, danger, fold }, className)} style={block ? { width: "100%", ...style } : style} {...rest} />;
}

/** El rótulo de un botón plegable (`fold`): se ve con ancho y, estrecho, solo lo lee el lector de pantalla. */
export function ButtonLabel({ children }: { children: ReactNode }) {
  return <span className="sk-btn-lbl">{children}</span>;
}
